/**
 * ReviewDocumentCanvas — the document, as the centre of the workspace.
 *
 * THIS IS THE CANVAS, not a pane. It was a docked pane for one release and
 * that was a half-measure: the centre still rendered a finding's detail, so
 * the same finding was on screen twice — once in the middle and once in the
 * list beside it — while the document, the thing actually being reviewed,
 * fought for a column against the lists.
 *
 * A review session is a document with objections to it. So the document takes
 * the centre, full-bleed and roomy, and the findings pane beside it carries
 * the list AND the detail. Nothing is duplicated: the centre never draws a
 * finding, and the pane is the only place one is read or decided.
 *
 * This holds NO view state. Original/Revised, Clean/Redline and the scroll
 * target all come from the session, because the toolbar strip that sets them
 * lives outside the canvas and survives it being expanded to the whole window.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { PiFileTextLight } from 'react-icons/pi';
import DocumentViewerModal from './DocumentViewerModal';
import DocumentZoom from './DocumentZoom';
import useReadingScale from './useReadingScale';
import { readingScalePx } from '../../services/preferences/readingScale';
import { RevisionStyle, ViewSource } from './documentView';

export interface ReviewDocumentTarget {
  documentId: number;
  documentName: string;
  /** Set when the canvas was pointed at a finding: scroll there and mark it. */
  scrollToFindingUuid?: string;
  scrollSearchText?: string;
  scrollSectionReference?: string;
  /** Bumped when the same finding is asked for again, to re-fire the scroll. */
  scrollNonce?: number;
}

interface Props {
  sessionUuid: string;
  /** Null while no document has been chosen — the empty canvas below. */
  target: ReviewDocumentTarget | null;
  viewSource: ViewSource;
  revisionStyle: RevisionStyle;
  /** Changes when any finding's decision changes — see `DocumentViewerModal`. */
  revisionToken?: string;
  /** A clause was clicked: the findings pane opens that finding. */
  onFindingSelect?: (findingUuid: string) => void;
}

const ReviewDocumentCanvas: React.FC<Props> = ({
  sessionUuid,
  target,
  viewSource,
  revisionStyle,
  revisionToken,
  onFindingSelect,
}) => {
  const { t } = useTranslation();
  /* One size for every document this user opens — see `readingScale`. */
  const reading = useReadingScale();

  /*
    Nothing chosen yet. Quiet and centred on the paper — the canvas IS the
    document surface, so an empty one should read as a blank page waiting for
    a document, not as a broken panel.
  */
  if (!target) {
    return (
      <div
        className="flex-1 min-h-0 flex flex-col items-center justify-center gap-3 text-center px-8"
        data-testid="review-document-canvas-empty"
      >
        <PiFileTextLight className="w-10 h-10 text-fg-muted" aria-hidden />
        <p className="text-sm font-medium text-fg-strong">
          {t('Select a document to review')}
        </p>
        <p className="text-xs text-fg-muted max-w-xs leading-relaxed">
          {t('Pick one from the documents list. Its findings appear beside it, and clicking a marked clause opens the finding for it.')}
        </p>
      </div>
    );
  }

  return (
    <div
      className="relative flex flex-col h-full min-h-0 overflow-hidden"
      data-testid="review-document-canvas"
    >
      <DocumentViewerModal
        /*
          Keyed by document, so switching documents remounts the viewer instead
          of leaving the previous blob, its converted HTML and its
          iframe-loaded flag behind — the scroll fires on mount, and a stale
          iframe would swallow it.
        */
        key={target.documentId}
        sessionUuid={sessionUuid}
        documentId={target.documentId}
        documentName={target.documentName}
        scrollToFindingUuid={target.scrollToFindingUuid}
        scrollSearchText={target.scrollSearchText}
        scrollSectionReference={target.scrollSectionReference}
        scrollNonce={target.scrollNonce}
        hideHeader
        viewSource={viewSource}
        revisionStyle={revisionStyle}
        revisionToken={revisionToken}
        onFindingSelect={onFindingSelect}
        readingScalePx={readingScalePx(reading.scale)}
      />

      {/* Over the document, out of the text's way. */}
      <DocumentZoom
        label={reading.label}
        onZoomIn={reading.zoomIn}
        onZoomOut={reading.zoomOut}
        onReset={reading.reset}
        canZoomIn={reading.canZoomIn}
        canZoomOut={reading.canZoomOut}
        isDefault={reading.isDefault}
      />
    </div>
  );
};

export default ReviewDocumentCanvas;
